/*
 * Dice Goblin ad tracking (round 28): "Booked a session".
 *
 * Shop events (product views, cart, checkout and purchases) are the Shopify channel apps' job: Facebook & Instagram,
 * Google & YouTube and TikTok each send theirs from their own pixel, server-side too. None of them can see a booking or
 * sign-up made through the Lair, and bookings are what the ads are for. So this file tells each platform whose ID is
 * set in Theme settings › Ad tracking: Meta and TikTok get their standard "Schedule" event, and Google Ads gets the
 * "Booked a session" conversion (plus "First booking" when it's someone's first). Each booking is sent once: its code
 * is the event ID, so a platform that sees it twice counts it once.
 *
 * The Lair decides what counts (`track` on a booking's answer: confirmed bookings people make themselves, src/newplayers.js
 * in the Lair) and assets/lair-core.js passes it on as a 'lair:ad-booked' event (its own name: lair-booking.js's
 * lair:booked is the page's, demo mode too). Nothing loads until there's something to send, apart from someone arriving
 * from an ad:
 * - from a Google ad (gclid, gbraid or wbraid in the link): Google's tag loads on that page, without a page view, so it
 *   keeps the click and a booking days later is credited to the ad;
 * - from a TikTok ad (ttclid): TikTok's pixel loads the same way, and keeps the click in its own cookie;
 * - from a Facebook or Instagram ad (fbclid): the click goes in Meta's own _fbc cookie, in Meta's format, with no script.
 * A visitor who has said no to marketing (Shopify's customer privacy) is told nothing, and their clicks aren't kept: the
 * page asks Shopify for its privacy API first and waits for it, and does nothing if it never comes. In demo mode (the
 * lab) layout/theme.liquid doesn't load this file at all.
 */
(() => {
  'use strict';

  /** The IDs from Theme settings (layout/theme.liquid's #ad-tracking), each checked; a bad or empty one switches it off */
  const settings = (() => {
    let raw = {};
    try {
      raw = JSON.parse(document.getElementById('ad-tracking')?.textContent || '{}') || {};
    } catch {
      raw = {};
    }
    const pick = (value, pattern) => (typeof value === 'string' && pattern.test(value.trim()) ? value.trim() : '');
    return {
      meta: pick(raw.meta, /^\d{6,20}$/),
      googleAds: pick(raw.googleAds, /^AW-\d{6,15}$/),
      googleBooked: pick(raw.googleBooked, /^[\w-]{4,40}$/),
      googleFirst: pick(raw.googleFirst, /^[\w-]{4,40}$/),
      tiktok: pick(raw.tiktok, /^[A-Za-z0-9]{10,30}$/),
    };
  })();
  const google = Boolean(settings.googleAds && settings.googleBooked);
  if (!settings.meta && !google && !settings.tiktok) return;

  /**
   * Runs fn once Shopify's privacy choice can be read: straight away when its API is here (or this isn't a Shopify page),
   * else once Shopify has loaded it (from round 28's review: read too early, it isn't there yet and would say yes).
   * Nothing runs if it can't be loaded.
   */
  const whenPrivacyKnown = (fn) => {
    const shopify = window.Shopify;
    if (!shopify || shopify.customerPrivacy || typeof shopify.loadFeatures !== 'function') {
      fn();
      return;
    }
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error && window.Shopify && window.Shopify.customerPrivacy) fn();
      });
    } catch {
      /* no privacy API: nothing is kept or sent */
    }
  };
  /** Shopify's customer privacy says no to marketing: then nothing is sent or kept */
  const allowed = () => {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.marketingAllowed === 'function') return privacy.marketingAllowed() !== false;
    } catch {
      /* unknown: as allowed, the default for New Zealand */
    }
    return true;
  };

  const addScript = (src) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  };

  /* ---------------- each platform's own loader (their standard snippets), set up once ---------------- */

  let googleOn = false;
  /** Google's tag for the Google Ads account, with no page view (the Google & YouTube app sends those) */
  const gtag = () => {
    if (!googleOn) {
      googleOn = true;
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() {
          window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
        };
      }
      window.gtag('js', new Date());
      window.gtag('config', settings.googleAds, { send_page_view: false });
      addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(settings.googleAds)}`);
    }
    return window.gtag;
  };

  let metaOn = false;
  /** The Meta pixel, with automatic events off and no page view (the Facebook & Instagram app sends those) */
  const fbq = () => {
    if (!window.fbq) {
      const stub = function fbqStub() {
        if (stub.callMethod) stub.callMethod.apply(stub, arguments); // eslint-disable-line prefer-rest-params
        else stub.queue.push(arguments); // eslint-disable-line prefer-rest-params
      };
      if (!window._fbq) window._fbq = stub;
      stub.push = stub;
      stub.loaded = true;
      stub.version = '2.0';
      stub.queue = [];
      window.fbq = stub;
      addScript('https://connect.facebook.net/en_US/fbevents.js');
    }
    if (!metaOn) {
      metaOn = true;
      window.fbq('set', 'autoConfig', false, settings.meta);
      window.fbq('init', settings.meta);
    }
    return window.fbq;
  };

  let tiktokOn = false;
  /** TikTok's pixel, loaded without a page view (the TikTok app sends those) → this pixel's own instance */
  const ttq = () => {
    if (!tiktokOn) {
      tiktokOn = true;
      const name = 'ttq';
      window.TiktokAnalyticsObject = name;
      const q = (window[name] = window[name] || []);
      q.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
      q.setAndDefer = (target, method) => {
        target[method] = function deferred() {
          target.push([method].concat(Array.prototype.slice.call(arguments, 0))); // eslint-disable-line prefer-rest-params
        };
      };
      q.methods.forEach((method) => q.setAndDefer(q, method));
      q.instance = (id) => {
        const instance = (q._i && q._i[id]) || [];
        q.methods.forEach((method) => q.setAndDefer(instance, method));
        return instance;
      };
      q.load = (id, options) => {
        const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        q._i = q._i || {};
        q._i[id] = [];
        q._i[id]._u = url;
        q._t = q._t || {};
        q._t[id] = Number(new Date());
        q._o = q._o || {};
        q._o[id] = options || {};
        addScript(`${url}?sdkid=${encodeURIComponent(id)}&lib=${name}`);
      };
      q.load(settings.tiktok);
    }
    return window.ttq.instance(settings.tiktok);
  };

  /* ---------------- arriving from an ad: keep the click ---------------- */

  const params = (() => {
    try {
      return new URL(window.location.href).searchParams;
    } catch {
      return new URLSearchParams();
    }
  })();
  const clickId = (name) => {
    const value = (params.get(name) || '').trim();
    return /^[\w.~-]{6,500}$/.test(value) ? value : '';
  };
  const fromGoogle = google && Boolean(clickId('gclid') || clickId('gbraid') || clickId('wbraid'));
  const fromTiktok = Boolean(settings.tiktok && clickId('ttclid'));
  const fbclid = settings.meta ? clickId('fbclid') : '';
  if (fromGoogle || fromTiktok || fbclid) {
    whenPrivacyKnown(() => {
      if (!allowed()) return;
      if (fromGoogle) gtag();
      if (fromTiktok) ttq();
      if (fbclid) {
        // Meta's click cookie: fb.1.<when, ms>.<fbclid>, 90 days. A cookie already holding this click stays as it is.
        const current = (document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/) || [])[1] || '';
        if (!current.endsWith(`.${fbclid}`)) {
          document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; max-age=${90 * 24 * 3600}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
        }
      }
    });
  }

  /* ---------------- a booking the Lair confirmed ---------------- */

  const sent = new Set();
  document.addEventListener('lair:ad-booked', (event) => {
    const track = event && event.detail;
    if (!track || typeof track !== 'object') return;
    const id = String(track.id || '').trim();
    if (!/^[\w-]{2,60}$/.test(id) || sent.has(id)) return;
    sent.add(id);
    const value = Math.max(0, Math.round((Number(track.value) || 0) * 100) / 100);
    const currency = /^[A-Z]{3}$/.test(String(track.currency || '')) ? track.currency : 'NZD';
    const kind = String(track.kind || 'booking').slice(0, 20);
    // first: true, false, or null when nobody knows (a guest's booking): never told as a repeat when it isn't known
    const first = track.first === true ? true : track.first === false ? false : null;
    // for any Shopify pixel that wants it (Settings › Customer events): the same news, published once
    try {
      window.Shopify?.analytics?.publish?.('dice_goblin:booked', { id, kind, value, currency, first });
    } catch {
      /* no Shopify analytics on this page */
    }
    whenPrivacyKnown(() => {
      if (!allowed()) return;
      if (settings.meta) {
        const data = { value, currency, content_category: kind, ...(first === null ? {} : { first_booking: first }) };
        fbq()('trackSingle', settings.meta, 'Schedule', data, { eventID: `booked-${id}` });
      }
      if (google) {
        const send = gtag();
        send('event', 'conversion', { send_to: `${settings.googleAds}/${settings.googleBooked}`, value, currency, transaction_id: id });
        if (first === true && settings.googleFirst) {
          send('event', 'conversion', { send_to: `${settings.googleAds}/${settings.googleFirst}`, value, currency, transaction_id: `first-${id}` });
        }
      }
      if (settings.tiktok) {
        ttq().track('Schedule', { value, currency, content_type: 'product', content_id: kind, description: first === true ? 'first booking' : 'booking' }, { event_id: `booked-${id}` });
      }
    });
  });
})();
